"use client"

import styled from "@emotion/styled"
import { Button, Tab, Tabs } from "@mui/material"
import { useTranslations } from "next-intl"
import Link from "next/link"
import { useMemo, useState } from "react"
import { Container, NewText } from "ui"
import { sharedColors } from "ui/theme/colors"
import {
  Apartment,
  PriorityData,
  ProjectPage,
  filterClick,
  replaceMediaUrls,
  wrapTables,
} from "utils"
import { ProjectsMessages } from "../Projects"
import WhiteBgHeading from "./ApartmentMarketingForRent/WhiteBgHeading"
import PropertyTab from "./PropertyTab"

const StyledContainer = styled.div<{ $paddingTop: boolean; $paddingBottom?: boolean }>`
  font-family: var(--narkis) !important;
  padding-top: ${(p) => (p.$paddingTop ? "60px" : "32px")};
  padding-bottom: ${(p) => (p.$paddingBottom ? "60px" : "32px")};
  gap: 32px;
  display: flex;
  flex-direction: column;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-top: ${(p) => (p.$paddingTop ? "60px" : "0px")};
    padding-bottom: ${(p) => (p.$paddingBottom ? "60px" : "0px")};
    gap: 24px;
  }
`
const TopText = styled.div<{ textCenter?: boolean }>`
  display: flex;
  flex-direction: column;
  text-align: center;
  margin: auto;
  gap: 8px;
  align-items: ${(props) => (props.textCenter ? "center" : "initial")};
  ${(props) => props.theme.breakpoints.down("sm")} {
    ul {
      padding-right: 16px;
      padding-left: 16px;
    }
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 16px;
    text-align: left;
    margin: 0;
  }
`

const TabsContainer = styled(Tabs)`
  .MuiTabs-flexContainer {
    flex-wrap: wrap;
    gap: 8px;
    justify-content: center;
    ${(props) => props.theme.breakpoints.up("md")} {
      gap: 16px;
      justify-content: flex-start;
    }
  }
`

const StyledTab = styled(Tab)<{ active: boolean }>`
  font-size: 16px;
  line-height: 20px;
  padding: 14px 16px;
  border-radius: 8px;
  color: black;
  font-weight: ${(props) => (props.active ? "600" : "400")};
  border: ${(props) => (props.active ? "2px" : "1px")} solid black;
  text-transform: none;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 18px;
    color: black;
    line-height: 25.92px;
    padding: 7px 16px;
  }
`
const ApartmentsContainer = styled.div<{ value: number }>`
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.value !== 0 ? "32px" : "0px")};
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: ${(props) => (props.value !== 0 ? "40px" : "0px")};
  }
`

export function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  }
}
type TabProps = {
  value: number
  index: number
  apartments: Apartment[]
  locale: string
  formSubjects: Record<string, string>
  messages: ProjectsMessages
  bmbyId?: number
  bmbyMeetingId?: number | string
  status?: any
  el?: any
  allApChar: string[]
  allBuChar: string[]
  imageGalleries: {
    apartmentThumbnail?: string
    apartmentGallery?: string[]
    numberOfRooms?: number
  }[]
  companyId?: number
  buildingCharacteristicsText?: string
  useDrawerLayout?: boolean
  fontFamilyVariable?: string
  phone?: string
  projectTitle?: string
  rentProjectInterestedNum?: number
  rentalProjects?: { name: string; id: number; rooms: string[] }[]
  cmsProjectLeadIds?: Record<string, number>
  leaveDetailsButtonText?: string
  noAvailableApartmentsBanner?: {
    title?: string
    text?: string
    button?: { name?: string; url?: string }
  }
  onEmptyStateCta?: () => void
  emailForLead?: string
  leadFormEnabled?: boolean
}

function ActiveTab({
  index,
  value,
  apartments,
  locale,
  formSubjects,
  messages,
  bmbyId,
  status,
  allApChar,
  allBuChar,
  imageGalleries,
  companyId,
  useDrawerLayout,
  buildingCharacteristicsText,
  bmbyMeetingId,
  fontFamilyVariable,
  phone,
  projectTitle,
  rentProjectInterestedNum,
  rentalProjects,
  cmsProjectLeadIds,
  leaveDetailsButtonText,
  noAvailableApartmentsBanner,
  onEmptyStateCta,
  emailForLead,
  leadFormEnabled,
}: TabProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== 0 && value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      style={value === 0 && index === 0 && !useDrawerLayout ? { marginTop: "40px" } : {}}
      className={fontFamilyVariable || ""}
    >
      {(value === 0 || value === index) && (
        <PropertyTab
          companyId={companyId}
          apartments={apartments}
          imageGalleries={imageGalleries}
          locale={locale}
          formSubjects={formSubjects}
          messages={messages}
          showHeader={value !== 0 || index === 0}
          bmbyId={bmbyId}
          bmbyMeetingId={bmbyMeetingId}
          status={status}
          allApChar={allApChar}
          allBuChar={allBuChar}
          useDrawerLayout={useDrawerLayout}
          buildingCharacteristicsText={buildingCharacteristicsText}
          fontFamilyVariable={fontFamilyVariable}
          phone={phone}
          projectTitle={projectTitle}
          rentProjectInterestedNum={rentProjectInterestedNum}
          rentalProjects={rentalProjects}
          cmsProjectLeadIds={cmsProjectLeadIds}
          leaveDetailsButtonText={leaveDetailsButtonText}
          noAvailableApartmentsBanner={noAvailableApartmentsBanner}
          onEmptyStateCta={onEmptyStateCta}
          emailForLead={emailForLead}
          leadFormEnabled={leadFormEnabled}
        />
      )}
    </div>
  )
}

type Props = {
  projectData: ProjectPage
  priorityData: PriorityData
  locale: string
  formSubjects: Record<string, string>
  messages: ProjectsMessages
  allApChar: string[]
  allBuChar: string[]
  companyId?: number
  useDrawerLayout?: boolean
  is4Rent?: boolean
  fontFamilyVariable?: string
  rentProjectInterestedNum?: number
  projectFilterId?: string
  noAvailableApartmentsBanner?: {
    title?: string
    text?: string
    button?: { name?: string; url?: string }
  }
  onEmptyStateCta?: () => void
  leadFormEnabled?: boolean
  allRentalProjects?: { name: string; id: number; rooms: string[] }[]
  cmsProjectLeadIds?: Record<string, number>
}

const EmptyStateBanner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 32px;
  margin-top: 32px;
  border-radius: 16px;
  background-color: #2768a3;
  overflow: hidden;
  position: relative;
  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 200px;
    height: 200px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
  }
  &::before {
    bottom: -40px;
    left: -99px;
  }
  &::after {
    top: -16px;
    right: -75px;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-top: 40px;
    align-items: center;
    text-align: center;
  }
`

const EmptyStateCta = styled(Button)`
  border-radius: 200px;
  padding: 8px 16px;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  height: 48px;
  color: white;
  border: 2px solid white;
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    border-color: white;
  }
`

const ViewAllButton = styled(Button)`
  align-self: center;
  border-radius: 8px;
  padding: 12px 24px;
  text-decoration: none;
  font-size: 16px;
  margin-top: 16px;
`

function Apartments({
  projectData,
  locale,
  formSubjects,
  messages,
  priorityData,
  allApChar,
  allBuChar,
  companyId,
  useDrawerLayout,
  is4Rent,
  fontFamilyVariable,
  rentProjectInterestedNum,
  projectFilterId,
  noAvailableApartmentsBanner,
  onEmptyStateCta,
  leadFormEnabled,
  allRentalProjects,
  cmsProjectLeadIds,
}: Props) {
  const [value, setValue] = useState(0)
  const t = useTranslations("BuildingInfo")
  const { apartments, types } = priorityData

  // Build rental projects list for the form
  const rentalProjects = useMemo(() => {
    if (allRentalProjects && allRentalProjects.length > 0) return allRentalProjects
    const rooms = [...new Set(apartments.map((a) => a.numberOfRooms).filter(Boolean))] as string[]
    return [{
      name: projectData.projectTitle || "",
      id: projectData.leadFormBMBYId || Number(projectData.priorityID) || 0,
      rooms: rooms.sort((a, b) => Number(a) - Number(b)),
    }]
  }, [
    allRentalProjects, apartments,
    projectData.projectTitle, projectData.leadFormBMBYId,
    projectData.priorityID,
  ])

  // Only projects that appear in the rent Priority feed get the rental waiting
  // list form. On the corporate site rent projects carry the same projectType as
  // for-sale ones, and the form is built around the rental project set, so this
  // membership check is what keeps a for-sale project out of it.
  const isRentalProject = !!allRentalProjects?.some(
    (project) => project.name === projectData.projectTitle,
  )

  const title = useDrawerLayout
    ? projectData?.newTitleAboveProperty || ""
    : projectData?.titleAboveProperty

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
    if (types?.[newValue - 1]?.value) {
      filterClick(types[newValue - 1].value, "פילטר סוג פרוייקט בעמוד פרוייקטים")
    }
  }
  return (
    <Container>
      <StyledContainer
        $paddingTop={
          title?.length > 0 || projectData.textAboveProperty.length > 0 || apartments.length > 0
        }
        $paddingBottom={useDrawerLayout}
      >
        {(title?.length > 0 || projectData.textAboveProperty.length > 0) &&
          (useDrawerLayout ? (
            <WhiteBgHeading
              title={title}
              subTitle={projectData?.textAboveProperty}
              mobileMarginBottom={32}
              desktopMarginBottom={32}
              isSubTitleRichText={true}
            />
          ) : (
            <TopText>
              <NewText
                component={"h2"}
                desktopFontSize={32}
                desktopLineHeight={"36px"}
                mobileFontSize={24}
                mobileLineHeight={"28px"}
                color={sharedColors.black}
              >
                {title}
              </NewText>
              <NewText
                component={"div"}
                desktopFontSize={18}
                desktopLineHeight={"25.92px"}
                mobileFontSize={16}
                mobileLineHeight={"23px"}
                color={sharedColors.black}
                dangerouslySetInnerHTML={{
                  __html: wrapTables(replaceMediaUrls(projectData.textAboveProperty)),
                }}
              />
            </TopText>
          ))}
        {apartments.length === 0 && useDrawerLayout && (
          <EmptyStateBanner>
            <NewText
              component={"h3"}
              desktopFontSize={32}
              desktopLineHeight={"36px"}
              mobileFontSize={24}
              mobileLineHeight={"28px"}
              color={"white"}
              sx={{ maxWidth: 675 }}
            >
              {noAvailableApartmentsBanner?.title || t("noApartmentsTitle")}
            </NewText>
            <NewText
              component={"p"}
              desktopFontSize={18}
              desktopLineHeight={"normal"}
              mobileFontSize={16}
              mobileLineHeight={"normal"}
              color={"white"}
              sx={{ maxWidth: 535 }}
            >
              {noAvailableApartmentsBanner?.text || t("noApartmentsText")}
            </NewText>
            {onEmptyStateCta && (
              <EmptyStateCta variant="outlined" onClick={onEmptyStateCta}>
                {noAvailableApartmentsBanner?.button?.name || t("joinWaitingList")}
              </EmptyStateCta>
            )}
          </EmptyStateBanner>
        )}
        {apartments.length > 0 && (
          <ApartmentsContainer value={value}>
            {types.length > 1 && (
              <TabsContainer value={value} onChange={handleChange} aria-label="property types">
                <StyledTab
                  active={value === 0}
                  key={"all"}
                  label={t("showAll")}
                  style={{ fontSize: 20 }}
                />
                {types.map((el, index) => (
                  <StyledTab
                    active={index + 1 === value}
                    key={index + 1}
                    label={el.value}
                    {...a11yProps(index)}
                    style={{ fontSize: 20 }}
                  />
                ))}
              </TabsContainer>
            )}
            {value === 0 ? (
              <ActiveTab
                locale={locale}
                apartments={apartments}
                imageGalleries={projectData.imageGalleries}
                index={0}
                value={value}
                formSubjects={formSubjects}
                messages={messages}
                bmbyId={projectData?.leadFormBMBYId || Number(projectData?.priorityID)}
                phone={projectData?.phone}
                projectTitle={projectData?.projectTitle}
                status={projectData?.projectStatuses[0]?.name}
                allApChar={allApChar}
                allBuChar={allBuChar}
                companyId={companyId}
                useDrawerLayout={useDrawerLayout}
                buildingCharacteristicsText={projectData?.buildingCharacteristicsText}
                bmbyMeetingId={projectData.bmbyMeetingId}
                fontFamilyVariable={fontFamilyVariable}
                rentProjectInterestedNum={rentProjectInterestedNum}
                rentalProjects={rentalProjects}
                cmsProjectLeadIds={cmsProjectLeadIds}
                leaveDetailsButtonText={projectData?.leaveDetailsButton}
                noAvailableApartmentsBanner={noAvailableApartmentsBanner}
                onEmptyStateCta={onEmptyStateCta}
                emailForLead={projectData?.emailForLead}
                leadFormEnabled={leadFormEnabled && isRentalProject}
              />
            ) : (
              types.map((el, index) => {
                return (
                  <ActiveTab
                    key={index}
                    imageGalleries={projectData.imageGalleries}
                    locale={locale}
                    apartments={apartments.filter((ap) => ap.propertyType === el.value)}
                    index={index + 1}
                    value={value}
                    el={el}
                    formSubjects={formSubjects}
                    messages={messages}
                    bmbyId={projectData?.leadFormBMBYId || Number(projectData?.priorityID)}
                    status={projectData?.projectStatuses[0]?.name}
                    allApChar={allApChar}
                    allBuChar={allBuChar}
                    companyId={companyId}
                    useDrawerLayout={useDrawerLayout}
                    buildingCharacteristicsText={projectData?.buildingCharacteristicsText}
                    rentProjectInterestedNum={rentProjectInterestedNum}
                    leaveDetailsButtonText={projectData?.leaveDetailsButton}
                    noAvailableApartmentsBanner={noAvailableApartmentsBanner}
                    onEmptyStateCta={onEmptyStateCta}
                    emailForLead={projectData?.emailForLead}
                    leadFormEnabled={leadFormEnabled && isRentalProject}
                    projectTitle={projectData?.projectTitle}
                    rentalProjects={rentalProjects}
                    cmsProjectLeadIds={cmsProjectLeadIds}
                  />
                )
              })
            )}
          </ApartmentsContainer>
        )}
        {projectFilterId && useDrawerLayout && (is4Rent || process.env.RENT_SITE_URL) && (
          <ViewAllButton
            // @ts-ignore - MUI Button component prop typing
            component={Link}
            href={
              is4Rent
                ? `/${locale}/apartments-4-rent?project=${encodeURIComponent(projectFilterId)}`
                : `${process.env.RENT_SITE_URL}/${locale}/apartments-4-rent?project=${encodeURIComponent(projectFilterId)}`
            }
            variant="outlined"
            color="primary"
          >
            {t("viewAllApartments") || "אני רוצה לשכור"}
          </ViewAllButton>
        )}
      </StyledContainer>
    </Container>
  )
}

export default Apartments
